/**
 * Shared utilities and components for homepage blocks
 */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Eye, Star, Zap, Clock, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import SmartImage from '../SmartImage';

// Resolve image URL helper
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (url.startsWith('/')) {
    return url;
  }
  return `/${url}`;
};

// Horizontal scroll container with arrows
export const ScrollContainer = ({ 
  children, 
  scrollRef, 
  className = '' 
}) => {
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => scroll('left')}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 hover:bg-black text-white p-2 rounded-full shadow-lg"
        aria-label="Scroll left"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <div
        ref={scrollRef}
        className={`flex space-x-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4 px-2 sm:px-6 ${className}`}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>

      <button
        onClick={() => scroll('right')}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 hover:bg-black text-white p-2 rounded-full shadow-lg"
        aria-label="Scroll right"
      >
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
};

// Section header component
export const SectionHeader = ({ 
  title, 
  icon: Icon, 
  iconColor = 'text-orange-400',
  viewAllLink,
  viewAllText = 'View all →'
}) => (
  <div className="flex items-center justify-between mb-3 sm:mb-6 px-2 sm:px-6">
    <h2 className="text-lg sm:text-3xl font-black flex items-center gap-2">
      {Icon && <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${iconColor}`} />}
      {title}
    </h2>
    {viewAllLink && (
      <Link to={viewAllLink} className="text-orange-400 hover:text-orange-300 font-medium text-xs sm:text-base">
        {viewAllText}
      </Link>
    )}
  </div>
);

// Horizontal Series Card (for carousels)
export const SeriesCardHorizontal = ({ series, rank, trending, featured, settings = {} }) => {
  const coverImage = series?.cover_card_url || series?.cover_thumb_url || series?.cover_url;
  const lqipImage = series?.lqip_url || series?.cover_lqip_url;
  const cardScale = settings.card_scale || 1;
  const maxHeight = settings.max_image_height || 280;
  
  return (
    <Link to={`/series/${series?.id}`}>
      <Card 
        className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all duration-300 group"
        style={{ 
          minWidth: `${Math.round(200 * cardScale)}px`,
          transform: `scale(${cardScale})`,
          transformOrigin: 'top left'
        }}
      >
        <CardContent className="p-0">
          <div className="relative">
            {settings.show_rank !== false && rank && (
              <div className={`absolute top-2 left-2 z-10 bg-black/80 backdrop-blur-sm text-white font-black text-xl w-8 h-8 rounded-lg flex items-center justify-center border-2 ${featured ? 'border-yellow-500' : 'border-orange-500'}`}>
                {rank}
              </div>
            )}

            <SmartImage
              src={resolveImageUrl(coverImage)}
              lqipSrc={resolveImageUrl(lqipImage)}
              alt={series?.title}
              type="cover"
              className="rounded-t-lg group-hover:scale-105 transition-transform duration-300"
              style={{ height: `${maxHeight}px` }}
            />

            {trending && (
              <Badge className="absolute top-2 right-2 bg-red-500 text-white font-bold">
                <Zap className="w-3 h-3 mr-1" />
                HOT
              </Badge>
            )}
            {featured && (
              <Badge className="absolute top-2 right-2 bg-yellow-500 text-black font-bold">
                <Star className="w-3 h-3 mr-1" />
                FEATURED
              </Badge>
            )}
          </div>

          <div className="p-3">
            <h3 className="text-white font-bold mb-1 line-clamp-1 text-sm">{series?.title}</h3>
            <p className="text-gray-400 text-xs mb-1">{series?.genres?.slice(0, 2).join(', ')}</p>
            <div className="flex items-center text-gray-400 text-xs">
              <Eye className="w-3 h-3 mr-1" />
              {series?.metrics_cache?.views || 0} views
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// Vertical Series Card (for grids)
export const SeriesCardVertical = ({ series, showDate, compact, settings = {} }) => {
  const coverImage = series?.cover_card_url || series?.cover_thumb_url || series?.cover_url;
  const lqipImage = series?.lqip_url || series?.cover_lqip_url;
  const cardScale = settings.card_scale || 1;
  const maxHeight = settings.max_image_height || 240;
  
  return (
    <Link to={`/series/${series?.id}`} className="group">
      <div 
        className={compact ? 'min-w-[140px]' : ''}
        style={{ transform: `scale(${cardScale})`, transformOrigin: 'top left' }}
      >
        <div className="relative mb-1 sm:mb-2">
          <SmartImage
            src={resolveImageUrl(coverImage)}
            lqipSrc={resolveImageUrl(lqipImage)}
            alt={series?.title}
            type="cover"
            className="rounded group-hover:scale-105 transition-transform duration-300"
            style={{ height: `${maxHeight}px` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded" />
          
          {showDate && series?.created_at && (
            <Badge className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-green-500 text-white text-[10px] sm:text-xs px-1 py-0">
              NEW
            </Badge>
          )}
        </div>
        <h3 className="text-white font-medium text-[11px] sm:text-sm line-clamp-2 mb-0.5 sm:mb-1">{series?.title}</h3>
        {!compact && (
          <>
            <p className="text-gray-400 text-[10px] sm:text-xs line-clamp-1">{series?.genres?.slice(0, 2).join(', ')}</p>
            {showDate && series?.created_at && (
              <p className="text-gray-500 text-[10px] sm:text-xs mt-0.5 sm:mt-1 hidden sm:block">
                <Clock className="w-3 h-3 inline mr-1" />
                {new Date(series.created_at).toLocaleDateString()}
              </p>
            )}
          </>
        )}
      </div>
    </Link>
  );
};

// Default export with all utilities
export default {
  resolveImageUrl,
  ScrollContainer,
  SectionHeader,
  SeriesCardHorizontal,
  SeriesCardVertical,
};
